import axios from 'axios';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import UserModel from '../../models/user';
import {
  authService,
  type LoginCredentials,
  type LoginResponse,
} from '../../services';
import { setAxiosToken } from '../../services/request';

export type AuthUser = Partial<UserModel> & { displayName?: string };

const getUserDisplayName = (user: AuthUser) =>
  user.displayName || user.nhan_vien?.ho_va_ten || user.email || '';

interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

const getStoredUser = (): AuthUser | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const name = window.localStorage.getItem('user_name');
  if (!name) {
    return null;
  }

  return {
    displayName: name,
  } as AuthUser;
};

const getStoredToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem('access_token');
};

export const loginUser = createAsyncThunk<
  { user: AuthUser; token: string },
  LoginCredentials,
  { rejectValue: string }
>('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const result: LoginResponse = await authService.login(credentials);

    if (result.status !== 'success' || !result.token) {
      return rejectWithValue(result.message || 'Đăng nhập thất bại');
    }

    setAxiosToken(result.token);
    return {
      user: (result.user || ({ email: credentials.email } as AuthUser)) as AuthUser,
      token: result.token,
    };
  } catch (error) {
    if (axios.isAxiosError<LoginResponse>(error)) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Đăng nhập thất bại'
      );
    }

    return rejectWithValue(
      error instanceof Error ? error.message : 'Đăng nhập thất bại'
    );
  }
});

const initialState: AuthState = {
  isAuthenticated: Boolean(getStoredToken()),
  user: getStoredUser(),
  token: getStoredToken(),
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart(state) {
      state.loading = true;
      state.error = null;
    },
    loginSuccess(
      state,
      action: PayloadAction<{ user: AuthUser; token: string }>
    ) {
      state.loading = false;
      state.error = null;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;

      if (typeof window !== 'undefined') {
        window.localStorage.setItem('access_token', action.payload.token);
        window.localStorage.setItem(
          'user_name',
          getUserDisplayName(action.payload.user)
        );
        const userTypeKey = action.payload.user.user_type?.key_value;
        if (userTypeKey) {
          window.localStorage.setItem('user_type_key', userTypeKey);
        } else {
          window.localStorage.removeItem('user_type_key');
        }
      }
    },
    loginFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
    },
    logout(state) {
      state.loading = false;
      state.error = null;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;

      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('access_token');
        window.localStorage.removeItem('user_name');
        window.localStorage.removeItem('user_type_key');
      }
    },
    setUser(state, action: PayloadAction<AuthUser | null>) {
      state.user = action.payload;
      state.isAuthenticated = Boolean(action.payload);
    },
    setToken(state, action: PayloadAction<string | null>) {
      state.token = action.payload;
      state.isAuthenticated = Boolean(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;

        if (typeof window !== 'undefined') {
          window.localStorage.setItem('access_token', action.payload.token);
          window.localStorage.setItem(
            'user_name',
            getUserDisplayName(action.payload.user) || 'admin'
          );
          const userTypeKey = action.payload.user.user_type?.key_value;
          if (userTypeKey) {
            window.localStorage.setItem('user_type_key', userTypeKey);
          } else {
            window.localStorage.removeItem('user_type_key');
          }
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Đăng nhập thất bại';
      });
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  setUser,
  setToken,
} = authSlice.actions;

export default authSlice.reducer;
